import { pxToRem } from '@withjoy/joykit/theme';
import type { ComboboxButtonProps } from '@withjoy/joykit/components/Combobox/Combobox.types';
import type { CommandGroupProps } from '@withjoy/joykit/components/Command/Command.types';
import { TOOLBAR_PILL_PROPS } from '@shared/utils/style/toolbarPill';

export const CURRENCY_BUTTON_PROPS: ComboboxButtonProps = {
  intent: 'primary',
  shape: 'rounded',
  variant: 'ghost',
  size: 'sm',
  overrides: {
    Root: {
      props: {
        width: pxToRem(100),
        height: pxToRem(32),
        borderRadius: pxToRem(16),
        backgroundColor: 'mono2',
        typographyVariant: 'button2',
        color: 'mono14',
        gap: pxToRem(4),
        _hover: { backgroundColor: 'mono1' },
      },
    },
  },
} as const;

/** `buttonAppearance="pill"`: the toolbar pill, sized to its label. */
export const CURRENCY_PILL_BUTTON_PROPS: ComboboxButtonProps = {
  intent: 'neutral',
  shape: 'rounded',
  variant: 'outline',
  size: 'sm',
  overrides: {
    Root: {
      props: {
        ...TOOLBAR_PILL_PROPS,
        gap: pxToRem(4),
      },
    },
  },
} as const;

const GROUP_HEADING_PROPS = {
  fontSize: pxToRem(14),
  marginBottom: 3,
  color: 'mono12',
} as const;

export const GROUP_HEADING_OVERRIDES: CommandGroupProps['overrides'] = {
  GroupHeading: { props: GROUP_HEADING_PROPS },
};

// The button-mode List uses display:flex + flexWrap:wrap for a 3-column card grid.
// Command.Group introduces an intermediate wrapper that breaks this layout because
// the group becomes a single flex child. The Group override below ensures each group
// spans the full row (width: 100%) and overrides overflow:hidden from the base __css
// so all items remain visible. The __css targets the inner div[role="group"] to
// replicate the flex-wrap grid on desktop (>1024px) and single-column on mobile.
// Note: passing __css via props replaces the component's base __css, so we include
// the base group styles (padding, color) as direct props to preserve them.
export const BUTTON_MODE_GROUP_OVERRIDES: CommandGroupProps['overrides'] = {
  Group: {
    props: {
      width: '100%',
      overflow: 'visible',
      padding: 2,
      color: 'mono14',
      display: 'flex',
      flexDirection: 'column',
      __css: {
        '> [role="group"]': {
          display: 'flex',
          flexDirection: 'column',
          // md breakpoint (1024px) + 1px to target above-tablet viewports
          '@media screen and (min-width: 1025px)': {
            flexDirection: 'row',
            flexWrap: 'wrap',
            columnGap: pxToRem(40),
          },
        },
      },
    },
  },
  GroupHeading: { props: GROUP_HEADING_PROPS },
};
